import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const ROLE_CHECK_TIMEOUT = 10000;

export const useInstitutionAdminCheck = () => {
  const { user } = useAuth();
  const [isInstitutionAdmin, setIsInstitutionAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const lastCheckedUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timeoutId: NodeJS.Timeout | null = null;

    const checkRole = async () => {
      if (!user || user.id !== lastCheckedUserIdRef.current) {
        setIsInstitutionAdmin(false);
        setLoading(true);
        lastCheckedUserIdRef.current = user?.id || null;
      }

      if (!user) {
        setLoading(false);
        return;
      }

      timeoutId = setTimeout(() => {
        if (!cancelled) {
          console.warn('[useInstitutionAdminCheck] Safety timeout reached, forcing loading=false');
          setLoading(false);
        }
      }, ROLE_CHECK_TIMEOUT);

      try {
        const { data, error } = await supabase.rpc('has_role', {
          _user_id: user.id,
          _role: 'institution_admin'
        });

        if (cancelled) return;

        if (error) {
          console.error('[useInstitutionAdminCheck] Error:', error);
          setIsInstitutionAdmin(false);
        } else {
          setIsInstitutionAdmin(data === true);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('[useInstitutionAdminCheck] Exception:', error);
        setIsInstitutionAdmin(false);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    checkRole();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [user?.id]);

  return { isInstitutionAdmin, loading };
};
